import { useEffect, useRef, useState, useCallback } from "react";
import { Spin, Empty } from "antd";
import priceFormat from "../../utils/priceFormat";

// Format price for marker label (e.g., $21K, $1.5M)
const formatPriceShort = (price) => {
  if (!price || price === 0) return "$0";
  const numPrice = typeof price === "string" ? parseFloat(price) : price;
  if (isNaN(numPrice)) return "$0";

  if (numPrice >= 1000000) {
    const millions = numPrice / 1000000;
    return `$${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  if (numPrice >= 1000) {
    const thousands = Math.round(numPrice / 1000);
    return `$${thousands}K`;
  }
  return `$${Math.round(numPrice)}`;
};

// Load Google Maps script dynamically
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.google?.maps) {
      resolve(window.google.maps);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google.maps));
      existingScript.addEventListener("error", reject);
      return;
    }

    // Create and load script
    const script = document.createElement("script");
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("Failed to load Google Maps"));

    document.head.appendChild(script);
  });
};

const WhiteboardMap = ({ listings, onListingClick, loading }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);

  // Check if listing is live based on expiration date
  const getListingStatus = (expiration) => {
    if (!expiration) return { isLive: false, days: 0, isToday: false };
    const goLiveDate = new Date(parseInt(expiration, 10));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    goLiveDate.setHours(0, 0, 0, 0);
    const diffTime = goLiveDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return {
      isLive: diffDays <= 0,
      days: Math.abs(diffDays),
      isToday: diffDays === 0,
    };
  };

  // Load Google Maps API
  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => {
        setGoogleLoaded(true);
      })
      .catch((error) => {
        setMapError("Failed to load Google Maps. Please check your internet connection.");
        console.error("Google Maps load error:", error);
      });
  }, []);

  // Initialize map once Google is loaded
  useEffect(() => {
    if (!googleLoaded || !mapRef.current || mapInstanceRef.current) return;

    try {
      // Default to US center if no listings with coordinates
      const defaultCenter = { lat: 39.8283, lng: -98.5795 }; // Center of US
      const defaultZoom = 4;

      const map = new window.google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: defaultZoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      });

      mapInstanceRef.current = map;
      infoWindowRef.current = new window.google.maps.InfoWindow();

      // Close info window when clicking on map
      map.addListener("click", () => {
        infoWindowRef.current.close();
        setSelectedListing(null);
      });
    } catch (error) {
      setMapError("Failed to initialize map: " + error.message);
    }

    return () => {
      markersRef.current.forEach((marker) => {
        if (marker) marker.setMap(null);
      });
      markersRef.current = [];
    };
  }, [googleLoaded]);

  // Update markers when listings change
  const updateMarkers = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.google?.maps) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => {
      if (marker) marker.setMap(null);
    });
    markersRef.current = [];

    // Filter listings with valid coordinates
    // Coordinates are stored as [longitude, latitude] (GeoJSON format)
    const validListings = listings.filter((listing) => {
      if (!listing.coordinates || listing.coordinates.length < 2) return false;

      const lng = parseFloat(listing.coordinates[0]);
      const lat = parseFloat(listing.coordinates[1]);

      // Check for valid numbers
      if (isNaN(lng) || isNaN(lat)) return false;

      // Filter out 0,0 coordinates (ocean/invalid)
      if (lat === 0 && lng === 0) return false;

      // Validate coordinate ranges (lat: -90 to 90, lng: -180 to 180)
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;

      return true;
    });

    if (validListings.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();

    validListings.forEach((listing) => {
      // Coordinates are stored as [longitude, latitude] (GeoJSON format)
      const lat = parseFloat(listing.coordinates[1]);
      const lng = parseFloat(listing.coordinates[0]);
      const position = { lat, lng };
      bounds.extend(position);

      // Create custom marker with price label
      const priceLabel = formatPriceShort(listing.price);

      // Create marker with custom SVG icon (olive/green price tag)
      const marker = new window.google.maps.Marker({
        position,
        map,
        label: {
          text: priceLabel,
          color: "#FFFFFF",
          fontSize: "11px",
          fontWeight: "bold",
        },
        icon: {
          url: `data:image/svg+xml,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="56" height="24">
              <rect x="0" y="0" width="56" height="24" rx="4" fill="#b4c540"/>
            </svg>
          `)}`,
          scaledSize: new window.google.maps.Size(56, 24),
          anchor: new window.google.maps.Point(28, 12),
          labelOrigin: new window.google.maps.Point(28, 12),
        },
      });

      // Click handler for marker
      marker.addListener("click", () => {
        setSelectedListing(listing);

        const { isLive, days, isToday } = getListingStatus(listing.expiration);

        // Create info window content matching mobile app style
        const content = `
          <div style="padding: 12px; min-width: 260px; max-width: 320px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            <div style="display: flex; justify-content: space-between; align-items: flex-start;">
              <div style="flex: 1;">
                <div style="font-size: 16px; font-weight: bold; color: #000; margin-bottom: 4px;">
                  $${listing.price ? priceFormat(listing.price) : ""}
                </div>
                <div style="font-size: 13px; color: #333; margin-bottom: 6px; line-height: 1.3;">
                  ${listing.streetAddr || ""} ${listing.city || ""} ${listing.zip || ""}
                </div>
                <div style="font-size: 12px; color: #666;">
                  ${listing.beds || 0} bd &nbsp;&nbsp; ${listing.baths || 0} ba &nbsp;&nbsp; ${listing.sqFt ? listing.sqFt.toLocaleString() : 0} sqft
                </div>
              </div>
              <div style="text-align: center; margin-left: 12px; min-width: 70px;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="${isLive ? '#b4c540' : '#3687c9'}">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                <div style="font-size: 10px; font-weight: 600; color: ${isLive ? '#b4c540' : '#3687c9'}; margin-top: 2px; line-height: 1.2;">
                  ${isLive ? 'LIVE on MLS' : 'Going Live In'}
                </div>
                <div style="font-size: 10px; color: #666;">
                  ${isToday ? 'TODAY' : isLive ? `${days} ${days === 1 ? 'DAY' : 'DAYS'} AGO` : `${days} ${days === 1 ? 'DAY' : 'DAYS'}`}
                </div>
              </div>
            </div>
          </div>
        `;

        infoWindowRef.current.setContent(content);
        infoWindowRef.current.open(map, marker);
      });

      markersRef.current.push(marker);
    });

    // Fit map to show all markers with padding
    if (validListings.length > 0) {
      // Add padding around the bounds
      const padding = { top: 50, right: 50, bottom: 50, left: 50 };
      map.fitBounds(bounds, padding);

      // Wait for map to be idle, then adjust zoom if needed
      window.google.maps.event.addListenerOnce(map, "idle", () => {
        const currentZoom = map.getZoom();
        // Don't zoom too far in for single marker or close markers
        if (currentZoom > 15) {
          map.setZoom(15);
        }
        // Don't zoom too far out
        if (currentZoom < 3) {
          map.setZoom(4);
        }
      });
    }
  }, [listings]);

  useEffect(() => {
    if (mapInstanceRef.current && googleLoaded) {
      updateMarkers();
    }
  }, [updateMarkers, listings, googleLoaded]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[500px] bg-gray-50 rounded-lg">
        <Spin size="large" />
      </div>
    );
  }

  // Loading Google Maps
  if (!googleLoaded && !mapError) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px] bg-gray-50 rounded-lg">
        <Spin size="large" />
        <p className="mt-4 text-gray-500">Loading map...</p>
      </div>
    );
  }

  // Error state
  if (mapError) {
    return (
      <div className="flex items-center justify-center h-[500px] bg-gray-50 rounded-lg">
        <div className="text-center">
          <p className="text-red-500 mb-2">{mapError}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-blue-500 underline"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // No listings
  if (!listings || listings.length === 0) {
    return (
      <div className="flex items-center justify-center h-[500px] bg-gray-50 rounded-lg">
        <Empty description="No listings to display on map" />
      </div>
    );
  }

  // Count valid listings (using same validation logic as updateMarkers)
  const validCount = listings.filter((l) => {
    if (!l.coordinates || l.coordinates.length < 2) return false;
    const lng = parseFloat(l.coordinates[0]);
    const lat = parseFloat(l.coordinates[1]);
    if (isNaN(lng) || isNaN(lat)) return false;
    if (lat === 0 && lng === 0) return false;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
    return true;
  }).length;

  return (
    <div className="relative">
      {/* Map Container */}
      <div
        ref={mapRef}
        className="w-full h-[500px] rounded-lg border border-gray-200"
      />

      {/* Listing Count Badge */}
      <div className="absolute top-4 left-4 bg-white px-3 py-1 rounded-full shadow text-sm text-gray-600">
        {validCount} listing{validCount !== 1 ? "s" : ""} on map
      </div>
    </div>
  );
};

export default WhiteboardMap;
